﻿import React, { CSSProperties, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { ButtonProps} from 'antd';

import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';

import { PickReports } from '../../models/pickReports/IPickReports';
import { IPickReportQuery, IPickReportQueryAdditional, IPickReportQueryExtended, PickReportUrl } from '../../models/pickReports/IFetchPickReports';
import PickReportButton from '../../components/pickReport/internal/PickReportButton';
import PickReportMarker from '../../components/pickReport/internal/PickReportMarker';
import { useOpenLayerPickReportControl } from './hooks/useOpenLayerPickReportControl';
import { usePickReportsFetcher } from './hooks/usePickReportsFetcher';
import { SpacesUrl } from '../../models/pickReports/IFetchPickReportSpaces';


export interface Props extends IPickReportQueryAdditional {
    /** Required for mouse click interaction and for adding PickReportControl to map. */
    map: Map | undefined,    

    /** Url for fetching pick reports. Can be url string, function or a class implemented from IFetchPickReports. */
    pickReportUrl: PickReportUrl,

    /** Url for fetching space features. */
    spacesUrl?: SpacesUrl,  

    externalLayerUrl?: string,

    /** Triggered when user clicks on PickReportControl. */
    activeChanged?: (active: boolean) => void,
    /** Triggered when user clicks on map while control is active. */
    queryChanged?: (query: IPickReportQueryExtended | undefined) => void,
    /** Triggered after user clicks on map while control is active. */
    reportsChanged: (reports: PickReports | undefined) => void,        

    /** Whether pick report interaction is active or not. */
    active?: boolean,
    /** Request used to fetch pick report. */
    query?: IPickReportQuery,      

    /** Includes depth value from depth layers in query. Default is true */
    includeDepth?: boolean,
    /** Includes features from spaces layers in query. Default is true */
    includeSpacesFeatures?: boolean,

    id?: string,
    className?: string,
    style?: CSSProperties,
    buttonProps?: Omit<ButtonProps, 'onClick' | 'loading'>,
    mobile?: boolean,
}

/**
 * Toggles pick report interaction and fetched pick reports when user clicks on map.
 * @param param0
 */
const PickReportControl: React.FC<Props> = ({
    map,
    token,
    layer,
    pickReportUrl,
    spacesUrl,
    externalLayerUrl,
    reportsChanged,
    queryChanged = () => { },
    activeChanged = () => { },
    active: activeInput,
    query: queryInput,
    includeDepth = true,
    includeSpacesFeatures = true,
    id,
    className,
    style,
    buttonProps,
    mobile   
}) => {

    const [busy, setBusy] = useState<boolean>();

    const [activeInternal, setActiveInternal] = useState<boolean>();
    const [coordinate, setCoordinate] = useState<Coordinate>();

    const { query: queryOutput, container, element } = useOpenLayerPickReportControl(map, activeInput, busy, includeDepth, includeSpacesFeatures);
    const { loading, reports } = usePickReportsFetcher(queryOutput, pickReportUrl, spacesUrl, externalLayerUrl, { layer, token });

    useEffect(() => {
        setBusy(loading);
    }, [loading]);
   
    useEffect(() => {        
        queryChanged(queryOutput);
        updateCoordinate(queryOutput);
    }, [queryOutput]);
   
    useEffect(() => {
        // query input will only be used to set coordinate, fetching a new pick report will only be done when map is clicked
        updateCoordinate(queryInput);
    }, [queryInput]);

    useEffect(() => {
        setActiveInternal(activeInput);
    }, [activeInput]);

    useEffect(() => {
        if (activeInternal === false) {
            queryChanged(undefined);
            reportsChanged(undefined);
        }            
    }, [activeInternal]);
   
    useEffect(() => {
        reportsChanged(reports);
    }, [reports]);    

    useEffect(() => {        
        return () => {            
            if (activeInternal === true)
                updateActive(false);
        };        
    }, []);

    const updateCoordinate = (query: IPickReportQuery | undefined) => {
        if (query) {
            const { lat, lon } = query;
            setCoordinate(fromLonLat([lon, lat]));
        }
        else
            setCoordinate(undefined);
    }   

    const updateActive = (value: boolean) => { 
        setActiveInternal(value);
        activeChanged(value);       
    }    

    return (
        <>
            <div id={id} className={className} ref={container}
                style={{
                    pointerEvents: 'auto',
                    position: 'absolute',
                    ...style            
                }}
            >
                <div ref={element}>
                    <PickReportButton
                        loading={loading}
                        active={!!activeInternal}
                        setActive={updateActive}
                        hideTooltip={mobile || buttonProps?.disabled}                       
                        {...buttonProps}
                    />                                     
                </div>
            </div>            

            {
                map && activeInternal && coordinate && <PickReportMarker
                    map={map}
                    coordinate={coordinate}
                />
            }
        </>
    )         
}

export default observer(PickReportControl);